import type {
  ApiAuditLog,
  ApiCategory,
  ApiCategoryDetail,
  ApiCategoryListItem,
  ApiCreateCategoryDetailRequest,
  ApiCreateCategoryRequest,
  ApiEmailMessageLog,
  ApiInAppNotification,
  ApiNotificationCatalogEvent,
  ApiNotificationPreference,
  ApiNotificationRoleDefault,
  ApiPagedResult,
  ApiPermissionCatalogItem,
  ApiPlant,
  ApiPushDevice,
  ApiRegisterPushDeviceRequest,
  ApiResetPasswordResult,
  ApiRole,
  ApiShift,
  ApiUpdateCategoryDetailRequest,
  ApiUpdateCategoryRequest,
  ApiUpsertMobileAppReleaseRequest,
  ApiMobileAppRelease,
  ApiMobileAppPlatformSummary,
  ApiMobileAppUpdateCheck,
  ApiUserDetail,
  ApiUserListItem,
} from "@tm-food/api-contract";
import type { HttpClient } from "@/shared/api/http";
import {
  buildQueryString,
  mapApiPagedResult,
  toApiQueryParams,
  type ListQueryParams,
  type PagedResult,
} from "@/shared/list/listQueryTypes";
import {
  mapAudit,
  mapCatalogEvent,
  mapCategory,
  mapCategoryDetail,
  mapCategoryListItem,
  mapMobileAppRelease,
  mapMobileAppPlatformSummary,
  mapEmailLog,
  mapInAppNotification,
  mapPermission,
  mapPlant,
  mapPreference,
  mapPushDevice,
  mapRole,
  mapRoleDefault,
  mapShift,
  mapUser,
  mapUserDetail,
  type AuditLog,
  type CreateCategoryDetailInput,
  type CreateCategoryInput,
  type SysCategory,
  type SysCategoryDetail,
  type SysCategoryListItem,
  type SysMobileAppRelease,
  type SysMobileAppPlatformSummary,
  type UpsertMobileAppReleaseInput,
  type UpdateCategoryDetailInput,
  type UpdateCategoryInput,
  type EmailMessageLog,
  type InAppNotification,
  type NotificationCatalogEvent,
  type NotificationPreference,
  type NotificationRoleDefault,
  type PermissionCatalogItem,
  type Plant,
  type PushDevice,
  type SysRole,
  type SysShift,
  type SysUser,
  type SysUserDetail,
} from "@/modules/sys/model/types";

export async function listPlants(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<Plant>> {
  const qs = buildQueryString(toApiQueryParams(params));
  const dto = await http.request < ApiPagedResult < ApiPlant >> (`/api/v1/sys/plants${qs}`);
  return mapApiPagedResult(dto, mapPlant);
}

export async function createPlant(
  http: HttpClient,
  body: { code: string; name: string; timezone?: string },
): Promise<Plant> {
  const dto = await http.request < ApiPlant > ("/api/v1/sys/plants", { method: "POST", body });
  return mapPlant(dto);
}

export async function updatePlant(
  http: HttpClient,
  id: string,
  body: { name: string; timezone?: string },
): Promise<Plant> {
  const dto = await http.request < ApiPlant > (`/api/v1/sys/plants/${id}`, {
    method: "PUT",
    body,
  });
  return mapPlant(dto);
}

export type DeletePlantsRangeResult = {
  deletedIds: string[];
  failures: { id: string; message: string }[];
};

export async function deletePlant(http: HttpClient, id: string): Promise<void> {
  await http.request(`/api/v1/sys/plants/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function deletePlantsRange(
  http: HttpClient,
  ids: string[],
): Promise<DeletePlantsRangeResult> {
  return http.request < DeletePlantsRangeResult > ("/api/v1/sys/plants/range", {
    method: "DELETE",
    body: ids,
  });
}

export async function setPlantStatus(
  http: HttpClient,
  id: string,
  body: { status: string },
): Promise<Plant> {
  const dto = await http.request < ApiPlant > (`/api/v1/sys/plants/${id}/status`, {
    method: "POST",
    body,
  });
  return mapPlant(dto);
}

export async function createUser(
  http: HttpClient,
  body: {
    username: string;
    fullName: string;
    password: string;
    defaultPlantId: string;
    roleCodes: string[];
    email?: string;
    department?: string;
  },
): Promise<SysUser> {
  const dto = await http.request < ApiUserListItem > ("/api/v1/sys/users", { method: "POST", body });
  return mapUser(dto);
}

export async function getUser(http: HttpClient, id: string): Promise<SysUserDetail> {
  const dto = await http.request < ApiUserDetail > (`/api/v1/sys/users/${encodeURIComponent(id)}`);
  return mapUserDetail(dto);
}

export async function updateUser(
  http: HttpClient,
  id: string,
  body: {
    fullName: string;
    defaultPlantId: string;
    roleCodes: string[];
    email?: string;
    department?: string;
  },
): Promise<SysUserDetail> {
  const dto = await http.request < ApiUserDetail > (`/api/v1/sys/users/${encodeURIComponent(id)}`, {
    method: "PUT",
    body,
  });
  return mapUserDetail(dto);
}

export type DeleteUsersRangeResult = {
  deletedIds: string[];
  failures: { id: string; message: string }[];
};

export async function deleteUser(http: HttpClient, id: string): Promise<void> {
  await http.request(`/api/v1/sys/users/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function deleteUsersRange(
  http: HttpClient,
  ids: string[],
): Promise<DeleteUsersRangeResult> {
  return http.request < DeleteUsersRangeResult > ("/api/v1/sys/users/range", {
    method: "DELETE",
    body: ids,
  });
}

export async function lockUser(http: HttpClient, id: string): Promise<SysUserDetail> {
  const dto = await http.request < ApiUserDetail > (
    `/api/v1/sys/users/${encodeURIComponent(id)}/lock`,
    { method: "POST" },
  );
  return mapUserDetail(dto);
}

export async function unlockUser(http: HttpClient, id: string): Promise<SysUserDetail> {
  const dto = await http.request < ApiUserDetail > (
    `/api/v1/sys/users/${encodeURIComponent(id)}/unlock`,
    { method: "POST" },
  );
  return mapUserDetail(dto);
}

export async function resetUserPassword(
  http: HttpClient,
  id: string,
): Promise<{ temporaryPassword: string }> {
  const dto = await http.request < ApiResetPasswordResult > (
    `/api/v1/sys/users/${encodeURIComponent(id)}/reset-password`,
    { method: "POST" },
  );
  return { temporaryPassword: dto.temporaryPassword };
}

export async function deleteRole(http: HttpClient, id: string): Promise<void> {
  await http.request(`/api/v1/sys/roles/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function createRole(
  http: HttpClient,
  body: {
    code: string;
    name: string;
    scope: string;
  },
): Promise<SysRole> {
  const dto = await http.request < ApiRole > ("/api/v1/sys/roles", { method: "POST", body });
  return mapRole(dto);
}

export async function getRole(http: HttpClient, id: string): Promise<SysRole> {
  const dto = await http.request < ApiRole > (`/api/v1/sys/roles/${encodeURIComponent(id)}`);
  return mapRole(dto);
}

export async function updateRole(
  http: HttpClient,
  id: string,
  body: { name: string; scope: string; status?: string },
): Promise<SysRole> {
  const dto = await http.request < ApiRole > (`/api/v1/sys/roles/${encodeURIComponent(id)}`, {
    method: "PUT",
    body,
  });
  return mapRole(dto);
}

export async function setRolePermissions(
  http: HttpClient,
  id: string,
  permissionCodes: string[],
): Promise<SysRole> {
  const dto = await http.request < ApiRole > (
    `/api/v1/sys/roles/${encodeURIComponent(id)}/permissions`,
    { method: "PUT", body: { permissionCodes } },
  );
  return mapRole(dto);
}

export async function listPermissions(http: HttpClient): Promise<PermissionCatalogItem[]> {
  const rows = await http.request<ApiPermissionCatalogItem[]>("/api/v1/sys/permissions");
  const mapped = rows.map(mapPermission);

  const fallbackDashboardPerms: PermissionCatalogItem[] = [
    {
      code: "bi.prod.list",
      module: "sys",
      action: "list",
      description: "Xem tổng quan sản xuất",
    },
    {
      code: "bi.qc.list",
      module: "sys",
      action: "list",
      description: "Xem tổng quan chất lượng",
    },
    {
      code: "bi.wh.list",
      module: "sys",
      action: "list",
      description: "Xem tổng quan kho",
    },
  ];

  const existingCodes = new Set(mapped.map((p) => p.code));
  for (const perm of fallbackDashboardPerms) {
    if (!existingCodes.has(perm.code)) {
      mapped.push(perm);
    }
  }

  return mapped;
}

export async function listShifts(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<SysShift>> {
  const qs = buildQueryString(toApiQueryParams(params));
  const dto = await http.request < ApiPagedResult < ApiShift >> (`/api/v1/sys/shifts${qs}`);
  return mapApiPagedResult(dto, mapShift);
}

export async function createShift(
  http: HttpClient,
  body: {
    plantCode: string;
    code: string;
    name: string;
    startTime: string;
    endTime: string;
  },
): Promise<SysShift> {
  const dto = await http.request < ApiShift > ("/api/v1/sys/shifts", {
    method: "POST",
    body,
  });
  return mapShift(dto);
}

export async function setShiftStatus(
  http: HttpClient,
  id: string,
  body: { status: string },
): Promise<SysShift> {
  const dto = await http.request < ApiShift > (`/api/v1/sys/shifts/${id}/status`, {
    method: "POST",
    body,
  });
  return mapShift(dto);
}

function mapCategoryPaged(dto: {
  items: ApiCategoryListItem[];
  page?: number;
  pageIndex?: number;
  pageSize: number;
  total?: number;
  totalItems?: number;
  totalPages?: number;
}): PagedResult<SysCategoryListItem> {
  const page = dto.page ?? dto.pageIndex ?? 1;
  const total = dto.total ?? dto.totalItems ?? 0;
  const pageSize = dto.pageSize;
  return {
    items: dto.items.map(mapCategoryListItem),
    page,
    pageSize,
    total,
    totalPages:
      dto.totalPages ?? (pageSize <= 0 ? 0 : Math.ceil(total / pageSize)),
  };
}

export async function listCategories(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<SysCategoryListItem>> {
  const qs = buildQueryString({
    ...toApiQueryParams(params),
    pageIndex: String(params.page),
  });
  const dto = await http.request < {
    items: ApiCategoryListItem[];
    page?: number;
    pageIndex?: number;
    pageSize: number;
    total?: number;
    totalItems?: number;
    totalPages?: number;
  } > (`/api/v1/sys/categories${qs}`);
  return mapCategoryPaged(dto);
}

export async function listCategoryDetailsByCode(
  http: HttpClient,
  code: string,
): Promise<SysCategoryDetail[]> {
  const dto = await http.request < ApiCategoryDetail[] > (
    `/api/v1/sys/categories/by-code/${encodeURIComponent(code)}/details`,
  );
  return dto.map(mapCategoryDetail);
}

export async function getCategory(
  http: HttpClient,
  id: string,
): Promise<SysCategory> {
  const dto = await http.request < ApiCategory > (
    `/api/v1/sys/categories/${encodeURIComponent(id)}`,
  );
  return mapCategory(dto);
}

export async function createCategory(
  http: HttpClient,
  body: CreateCategoryInput,
): Promise<SysCategory> {
  const payload: ApiCreateCategoryRequest = {
    code: body.code,
    name: body.name,
  };
  const dto = await http.request < ApiCategory > ("/api/v1/sys/categories", {
    method: "POST",
    body: payload,
  });
  return mapCategory(dto);
}

export async function updateCategory(
  http: HttpClient,
  id: string,
  body: UpdateCategoryInput,
): Promise<SysCategory> {
  const payload: ApiUpdateCategoryRequest = { name: body.name };
  const dto = await http.request < ApiCategory > (
    `/api/v1/sys/categories/${encodeURIComponent(id)}`,
    { method: "PUT", body: payload },
  );
  return mapCategory(dto);
}

export async function deleteCategory(
  http: HttpClient,
  id: string,
): Promise<void> {
  await http.request(`/api/v1/sys/categories/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function createCategoryDetail(
  http: HttpClient,
  categoryId: string,
  body: CreateCategoryDetailInput,
): Promise<SysCategoryDetail> {
  const payload: ApiCreateCategoryDetailRequest = {
    code: body.code,
    name: body.name,
    value: body.value ?? null,
    description: body.description ?? null,
    orderNo: body.orderNo ?? 0,
  };
  const dto = await http.request < ApiCategoryDetail > (
    `/api/v1/sys/categories/${encodeURIComponent(categoryId)}/details`,
    { method: "POST", body: payload },
  );
  return mapCategoryDetail(dto);
}

export async function updateCategoryDetail(
  http: HttpClient,
  categoryId: string,
  detailId: string,
  body: UpdateCategoryDetailInput,
): Promise<SysCategoryDetail> {
  const payload: ApiUpdateCategoryDetailRequest = {
    name: body.name,
    value: body.value ?? null,
    description: body.description ?? null,
    orderNo: body.orderNo ?? null,
  };
  const dto = await http.request < ApiCategoryDetail > (
    `/api/v1/sys/categories/${encodeURIComponent(categoryId)}/details/${encodeURIComponent(detailId)}`,
    { method: "PUT", body: payload },
  );
  return mapCategoryDetail(dto);
}

export async function deleteCategoryDetail(
  http: HttpClient,
  categoryId: string,
  detailId: string,
): Promise<void> {
  await http.request(
    `/api/v1/sys/categories/${encodeURIComponent(categoryId)}/details/${encodeURIComponent(detailId)}`,
    { method: "DELETE" },
  );
}

export async function listMobileAppPlatforms(
  http: HttpClient,
): Promise<SysMobileAppPlatformSummary[]> {
  const dto = await http.request < ApiMobileAppPlatformSummary[] > (
    "/api/v1/sys/mobile-app-releases/platforms",
  );
  return dto.map(mapMobileAppPlatformSummary);
}

export async function checkMobileAppUpdate(
  http: HttpClient,
  platform: "Android" | "Ios",
  version: string,
): Promise<ApiMobileAppUpdateCheck> {
  const qs = buildQueryString({ platform, version });
  return http.request<ApiMobileAppUpdateCheck>(
    `/api/v1/sys/mobile-app-releases/check${qs}`,
    { auth: false },
  );
}

export async function listMobileAppReleases(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<SysMobileAppRelease>> {
  const qs = buildQueryString(toApiQueryParams(params));
  const dto = await http.request < {
    items: ApiMobileAppRelease[];
    page?: number;
    pageIndex?: number;
    pageSize: number;
    total?: number;
    totalItems?: number;
    totalPages?: number;
  } > (`/api/v1/sys/mobile-app-releases${qs}`);
  const page = dto.page ?? dto.pageIndex ?? 1;
  const total = dto.total ?? dto.totalItems ?? 0;
  const pageSize = dto.pageSize;
  return {
    items: dto.items.map(mapMobileAppRelease),
    page,
    pageSize,
    total,
    totalPages: dto.totalPages ?? (pageSize <= 0 ? 0 : Math.ceil(total / pageSize)),
  };
}

export async function getMobileAppRelease(
  http: HttpClient,
  id: string,
): Promise<SysMobileAppRelease> {
  const dto = await http.request < ApiMobileAppRelease > (
    `/api/v1/sys/mobile-app-releases/${encodeURIComponent(id)}`,
  );
  return mapMobileAppRelease(dto);
}

function toReleasePayload(body: UpsertMobileAppReleaseInput): ApiUpsertMobileAppReleaseRequest {
  return {
    platform: body.platform,
    versionName: body.versionName.trim(),
    releaseNotes: body.releaseNotes.trim(),
    downloadUrl: body.downloadUrl.trim(),
    forceUpdate: body.forceUpdate,
  };
}

export async function createMobileAppRelease(
  http: HttpClient,
  body: UpsertMobileAppReleaseInput,
): Promise<SysMobileAppRelease> {
  const dto = await http.request < ApiMobileAppRelease > ("/api/v1/sys/mobile-app-releases", {
    method: "POST",
    body: toReleasePayload(body),
  });
  return mapMobileAppRelease(dto);
}

export async function updateMobileAppRelease(
  http: HttpClient,
  id: string,
  body: UpsertMobileAppReleaseInput,
): Promise<SysMobileAppRelease> {
  const dto = await http.request < ApiMobileAppRelease > (
    `/api/v1/sys/mobile-app-releases/${encodeURIComponent(id)}`,
    { method: "PUT", body: toReleasePayload(body) },
  );
  return mapMobileAppRelease(dto);
}

export async function deleteMobileAppRelease(http: HttpClient, id: string): Promise<void> {
  await http.request(`/api/v1/sys/mobile-app-releases/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function publishMobileAppRelease(
  http: HttpClient,
  id: string,
): Promise<SysMobileAppRelease> {
  const dto = await http.request < ApiMobileAppRelease > (
    `/api/v1/sys/mobile-app-releases/${encodeURIComponent(id)}/publish`,
    { method: "POST" },
  );
  return mapMobileAppRelease(dto);
}

export async function retireMobileAppRelease(
  http: HttpClient,
  id: string,
): Promise<SysMobileAppRelease> {
  const dto = await http.request < ApiMobileAppRelease > (
    `/api/v1/sys/mobile-app-releases/${encodeURIComponent(id)}/retire`,
    { method: "POST" },
  );
  return mapMobileAppRelease(dto);
}

export async function listUsers(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<SysUser>> {
  const qs = buildQueryString(toApiQueryParams(params));
  const dto = await http.request < ApiPagedResult < ApiUserListItem >> (`/api/v1/sys/users${qs}`);
  return mapApiPagedResult(dto, mapUser);
}

export async function listRoles(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<SysRole>> {
  const qs = buildQueryString(toApiQueryParams(params));
  const dto = await http.request < ApiPagedResult < ApiRole >> (`/api/v1/sys/roles${qs}`);
  return mapApiPagedResult(dto, mapRole);
}

export async function listAllRoles(http: HttpClient): Promise<SysRole[]> {
  const rows = await http.request < ApiRole[] > ("/api/v1/sys/roles/all");
  return rows.map(mapRole);
}

export async function listAudit(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<AuditLog>> {
  const qs = buildQueryString(toApiQueryParams(params));
  const dto = await http.request < ApiPagedResult < ApiAuditLog >> (`/api/v1/sys/audit${qs}`);
  return mapApiPagedResult(dto, mapAudit);
}

export async function listEmailLogs(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<EmailMessageLog>> {
  const qs = buildQueryString(toApiQueryParams(params));
  const dto = await http.request < ApiPagedResult < ApiEmailMessageLog >> (
    `/api/v1/sys/email-logs${qs}`,
  );
  return mapApiPagedResult(dto, mapEmailLog);
}

export async function listNotificationPreferences(
  http: HttpClient,
): Promise<NotificationPreference[]> {
  const rows = await http.request < ApiNotificationPreference[] > (
    "/api/v1/sys/notifications/preferences",
  );
  return rows.map(mapPreference);
}

export async function upsertNotificationPreferences(
  http: HttpClient,
  items: NotificationPreference[],
): Promise<NotificationPreference[]> {
  const rows = await http.request < ApiNotificationPreference[] > (
    "/api/v1/sys/notifications/preferences",
    {
      method: "PUT",
      body: { items },
    },
  );
  return rows.map(mapPreference);
}

export async function listNotificationCatalog(
  http: HttpClient,
  group?: string,
): Promise<NotificationCatalogEvent[]> {
  const qs = group ? buildQueryString({ group }) : "";
  const rows = await http.request < ApiNotificationCatalogEvent[] > (
    `/api/v1/sys/notifications/catalog${qs}`,
  );
  return rows.map(mapCatalogEvent);
}

export async function setNotificationEventEnabled(
  http: HttpClient,
  eventType: string,
  isEnabled: boolean,
): Promise<NotificationCatalogEvent> {
  const row = await http.request < ApiNotificationCatalogEvent > (
    `/api/v1/sys/notifications/catalog/${encodeURIComponent(eventType)}`,
    { method: "PUT", body: { isEnabled } },
  );
  return mapCatalogEvent(row);
}

export async function setNotificationGroupEnabled(
  http: HttpClient,
  groupCode: string,
  isEnabled: boolean,
): Promise<NotificationCatalogEvent[]> {
  const rows = await http.request < ApiNotificationCatalogEvent[] > (
    `/api/v1/sys/notifications/catalog/groups/${encodeURIComponent(groupCode)}`,
    { method: "PUT", body: { isEnabled } },
  );
  return rows.map(mapCatalogEvent);
}

export async function listNotificationRoleDefaults(
  http: HttpClient,
): Promise<NotificationRoleDefault[]> {
  const rows = await http.request < ApiNotificationRoleDefault[] > (
    "/api/v1/sys/notifications/role-defaults",
  );
  return rows.map(mapRoleDefault);
}

export async function listInAppNotifications(
  http: HttpClient,
  opts?: { take?: number; skip?: number; unreadOnly?: boolean },
): Promise<InAppNotification[]> {
  const params: Record<string, string> = {
    take: String(opts?.take ?? 50),
  };
  if (opts?.skip != null && opts.skip > 0) params.skip = String(opts.skip);
  if (opts?.unreadOnly) params.unreadOnly = "true";
  const qs = buildQueryString(params);
  const rows = await http.request < ApiInAppNotification[] > (`/api/v1/sys/notifications${qs}`);
  return rows.map(mapInAppNotification);
}

export async function markInAppNotificationRead(
  http: HttpClient,
  id: string,
): Promise<void> {
  await http.request(`/api/v1/sys/notifications/${encodeURIComponent(id)}/read`, {
    method: "POST",
  });
}

export async function markAllInAppNotificationsRead(http: HttpClient): Promise<void> {
  await http.request("/api/v1/sys/notifications/read-all", { method: "POST" });
}

export async function registerPushDevice(
  http: HttpClient,
  body: ApiRegisterPushDeviceRequest,
): Promise<PushDevice> {
  const row = await http.request < ApiPushDevice > ("/api/v1/sys/push/devices", {
    method: "POST",
    body,
  });
  return mapPushDevice(row);
}

export async function listMyPushDevices(http: HttpClient): Promise<PushDevice[]> {
  const rows = await http.request < ApiPushDevice[] > ("/api/v1/sys/push/devices");
  return rows.map(mapPushDevice);
}

export async function unregisterPushDevice(
  http: HttpClient,
  deviceToken: string,
): Promise<void> {
  await http.request(
    `/api/v1/sys/push/devices?deviceToken=${encodeURIComponent(deviceToken)}`,
    { method: "DELETE" },
  );
}

export type ProductionLineRow = {
  id: string;
  plantCode: string;
  code: string;
  name: string;
  status: string;
  createdAt: string;
};

export type DeleteLinesRangeResult = {
  deletedIds: string[];
  failures: { id: string; message: string }[];
};

export async function listProductionLines(
  http: HttpClient,
): Promise<ProductionLineRow[]> {
  return http.request < ProductionLineRow[] > ("/api/v1/sys/production-lines");
}

export async function deleteProductionLine(http: HttpClient, id: string): Promise<void> {
  await http.request(`/api/v1/sys/production-lines/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function deleteProductionLinesRange(
  http: HttpClient,
  ids: string[],
): Promise<DeleteLinesRangeResult> {
  return http.request < DeleteLinesRangeResult > ("/api/v1/sys/production-lines/range", {
    method: "DELETE",
    body: ids,
  });
}

export type AreaRow = {
  id: string;
  plantCode: string;
  lineCode: string;
  code: string;
  name: string;
  status: string;
  createdAt: string;
};

export type DeleteAreasRangeResult = {
  deletedIds: string[];
  failures: { id: string; message: string }[];
};

export async function listAreas(http: HttpClient): Promise<AreaRow[]> {
  return http.request < AreaRow[] > ("/api/v1/sys/areas");
}

export async function deleteArea(http: HttpClient, id: string): Promise<void> {
  await http.request(`/api/v1/sys/areas/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function deleteAreasRange(
  http: HttpClient,
  ids: string[],
): Promise<DeleteAreasRangeResult> {
  return http.request < DeleteAreasRangeResult > ("/api/v1/sys/areas/range", {
    method: "DELETE",
    body: ids,
  });
}

/** Create MFA challenge — returns challenge id (EXE approve). */
export async function createMfaChallenge(http: HttpClient, purpose: string): Promise<string> {
  const id = await http.request < string > ("/api/v1/sys/mfa/challenges", {
    method: "POST",
    body: { purpose },
  });
  return typeof id === "string" ? id : String(id);
}

export type MfaStatus = {
  enabled: boolean;
  hasSecret: boolean;
  username: string;
};

export type MfaEnrollStart = {
  secret: string;
  otpAuthUri: string;
  issuer: string;
};

export type MfaUserRow = {
  id: string;
  username: string;
  fullName: string;
  mfaEnabled: boolean;
};

export async function getMfaStatus(http: HttpClient): Promise<MfaStatus> {
  const d = await http.request < {
    enabled: boolean;
    hasSecret: boolean;
    username: string;
  } > ("/api/v1/sys/mfa/me");
  return {
    enabled: d.enabled,
    hasSecret: d.hasSecret,
    username: d.username,
  };
}

export async function startMfaEnroll(http: HttpClient): Promise<MfaEnrollStart> {
  const d = await http.request < {
    secret: string;
    otpAuthUri: string;
    issuer: string;
  } > ("/api/v1/sys/mfa/enroll/start", { method: "POST" });
  return {
    secret: d.secret,
    otpAuthUri: d.otpAuthUri,
    issuer: d.issuer,
  };
}

export async function confirmMfaEnroll(http: HttpClient, code: string): Promise<void> {
  await http.request("/api/v1/sys/mfa/enroll/confirm", {
    method: "POST",
    body: { code },
  });
}

export async function listMfaUsers(http: HttpClient): Promise<MfaUserRow[]> {
  const rows = await http.request <
    { id: string; username: string; fullName: string; mfaEnabled: boolean }[]
    > ("/api/v1/sys/mfa/users");
  return rows.map((r) => ({
    id: r.id,
    username: r.username,
    fullName: r.fullName,
    mfaEnabled: r.mfaEnabled,
  }));
}

export async function resetMfaUser(http: HttpClient, userId: string): Promise<void> {
  await http.request(`/api/v1/sys/mfa/users/${encodeURIComponent(userId)}/reset`, {
    method: "POST",
  });
}

export type NumberingRule = {
  documentType: string;
  prefix: string;
  dateFormat: string;
  separator: string;
  padLength: number;
  resetPolicy: string;
  nextValue: number;
  preview: string;
};

export type NumberingRuleInput = {
  prefix: string;
  dateFormat: string;
  separator: string;
  padLength: number;
  resetPolicy: string;
};

export type NumberingPreview = {
  documentType: string;
  code: string;
};

type NumberingRuleWire = {
  documentType: string;
  prefix: string;
  dateFormat: string;
  separator: string;
  padLength: number;
  resetPolicy: string;
  nextValue: number;
  preview: string;
};

function mapNumberingRule(row: NumberingRuleWire): NumberingRule {
  return {
    documentType: row.documentType,
    prefix: row.prefix,
    dateFormat: row.dateFormat,
    separator: row.separator,
    padLength: row.padLength,
    resetPolicy: row.resetPolicy,
    nextValue: row.nextValue,
    preview: row.preview,
  };
}

export async function listNumberingRules(
  http: HttpClient,
  params?: ListQueryParams,
): Promise<PagedResult<NumberingRule>> {
  const qs = params ? buildQueryString(toApiQueryParams(params)) : "";
  const res = await http.request<ApiPagedResult<NumberingRuleWire> | NumberingRuleWire[]>(
    `/api/v1/sys/numbering${qs}`,
  );
  if (Array.isArray(res)) {
    return {
      items: res.map(mapNumberingRule),
      page: 1,
      pageSize: res.length,
      total: res.length,
      totalPages: 1,
    };
  }
  return mapApiPagedResult(res, mapNumberingRule);
}

export async function previewNumberingRule(
  http: HttpClient,
  documentType: string,
): Promise<NumberingPreview> {
  const row = await http.request<{ documentType: string; code: string }>(
    `/api/v1/sys/numbering/${encodeURIComponent(documentType)}/preview`,
  );
  return { documentType: row.documentType, code: row.code };
}

export async function updateNumberingRule(
  http: HttpClient,
  documentType: string,
  body: NumberingRuleInput,
): Promise<NumberingRule> {
  const row = await http.request<NumberingRuleWire>(
    `/api/v1/sys/numbering/${encodeURIComponent(documentType)}`,
    {
      method: "PUT",
      body: {
        prefix: body.prefix,
        dateFormat: body.dateFormat,
        separator: body.separator,
        padLength: body.padLength,
        resetPolicy: body.resetPolicy,
      },
    },
  );
  return mapNumberingRule(row);
}

export async function resetNumberingRule(
  http: HttpClient,
  documentType: string,
  nextValue: number,
): Promise<NumberingRule> {
  const row = await http.request<NumberingRuleWire>(
    `/api/v1/sys/numbering/${encodeURIComponent(documentType)}/reset`,
    { method: "POST", body: { nextValue } },
  );
  return mapNumberingRule(row);
}

export type AppSetting = {
  id: string;
  key: string;
  label: string;
  description: string | null;
  valueType: string;
  value: string;
};

export type AppSettingInput = {
  key?: string;
  label: string;
  description: string | null;
  valueType: string;
  value: string;
};

export type ResolvedAppSetting = {
  key: string;
  valueType: string;
  value: string;
};

export async function listAppSettings(
  http: HttpClient,
  params?: ListQueryParams,
): Promise<PagedResult<AppSetting>> {
  const qs = params ? buildQueryString(toApiQueryParams(params)) : "";
  const res = await http.request<ApiPagedResult<AppSetting> | AppSetting[]>(
    `/api/v1/sys/settings${qs}`,
  );
  if (Array.isArray(res)) {
    return {
      items: res,
      page: 1,
      pageSize: res.length,
      total: res.length,
      totalPages: 1,
    };
  }
  return mapApiPagedResult(res, (x) => x);
}

export async function resolveAppSettings(
  http: HttpClient,
  keys: string[],
): Promise<ResolvedAppSetting[]> {
  const qs = new URLSearchParams({ keys: keys.join(",") });
  return http.request<ResolvedAppSetting[]>(`/api/v1/sys/settings/resolved?${qs}`);
}

export async function createAppSetting(
  http: HttpClient,
  body: AppSettingInput,
): Promise<AppSetting> {
  return http.request<AppSetting>("/api/v1/sys/settings", { method: "POST", body });
}

export async function updateAppSetting(
  http: HttpClient,
  id: string,
  body: AppSettingInput,
): Promise<AppSetting> {
  return http.request<AppSetting>(`/api/v1/sys/settings/${encodeURIComponent(id)}`, {
    method: "PUT",
    body,
  });
}

export async function deleteAppSetting(http: HttpClient, id: string): Promise<void> {
  await http.request(`/api/v1/sys/settings/${encodeURIComponent(id)}`, { method: "DELETE" });
}
